import { Show } from "@refinedev/antd";
import { useGetIdentity, useList, useNotification, useOne } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import { api } from "../api";
import {
  AUDIT_ACTIONS,
  AUDIT_TARGETS,
  COPY_TOOLTIPS,
  DATE_TIME_FORMAT,
  STAFF_STATUS,
} from "../labels";
import type { Me } from "../session";
import type { InvitationAnswer, Role, StaffMemberDetail } from "../types";

type ProfileValues = { name: string; phoneNumber?: string };

/** The invitation link right after it is created; it is never shown again. */
function InvitationLink({ answer, onClose }: { answer: InvitationAnswer; onClose: () => void }) {
  return (
    <Alert
      type="success"
      showIcon
      closable
      onClose={onClose}
      message={answer.smsSent ? "Davet bağlantısı SMS ile gönderildi." : "Davet bağlantısı hazır."}
      description={
        <>
          <Typography.Paragraph>
            Bu bağlantı 72 saat geçerlidir ve bir daha gösterilmez. Gerekirse güvenli bir kanaldan
            iletebilirsin.
          </Typography.Paragraph>
          <Typography.Paragraph
            copyable={{ tooltips: COPY_TOOLTIPS }}
            code
            data-testid="invitation-url"
            style={{ marginBottom: 0 }}
          >
            {answer.invitationUrl}
          </Typography.Paragraph>
        </>
      }
      style={{ marginBottom: 24 }}
    />
  );
}

/**
 * /team/:id: a staff member's details, roles, invitation, status and removal, and their latest
 * actions (permission `staff.manage`). Nobody changes their own access here.
 */
export function TeamMemberPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { open } = useNotification();
  const { data: me } = useGetIdentity<Me>();
  const { result: member, query } = useOne<StaffMemberDetail>({ resource: "staff", id });
  const { result: roles } = useList<Role>({ resource: "roles", pagination: { mode: "off" } });
  const [invitation, setInvitation] = useState<InvitationAnswer | null>(
    (location.state as InvitationAnswer | null) ?? null,
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [profileForm] = Form.useForm<ProfileValues>();
  const [rolesForm] = Form.useForm<{ roleIds: string[] }>();

  // The link from /team/new is shown once; reloading the page must not show it again.
  useEffect(() => {
    if (location.state) navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!member) return;
    profileForm.setFieldsValue({ name: member.name, phoneNumber: member.phoneNumber ?? "" });
    rolesForm.setFieldsValue({ roleIds: member.roles.map((role) => role.id) });
  }, [member, profileForm, rolesForm]);

  async function run<T>(key: string, action: () => Promise<T>, success?: string) {
    setBusy(key);
    try {
      const value = await action();
      if (success) open?.({ type: "success", message: success });
      return { ok: true as const, value };
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
      return { ok: false as const };
    } finally {
      setBusy(null);
    }
  }

  const refresh = () => query.refetch();
  const isSelf = member?.id === me?.id;

  async function newInvitation(sendSms: boolean) {
    const result = await run("invite", () =>
      api<InvitationAnswer>(`/api/office/staff/${id}/invitation`, {
        method: "POST",
        body: { sendSms },
      }),
    );
    if (result.ok) {
      setInvitation(result.value);
      await refresh();
    }
  }

  async function remove() {
    const result = await run("remove", () =>
      api<{ mode: "deleted" | "anonymized" }>(`/api/office/staff/${id}`, { method: "DELETE" }),
    );
    if (!result.ok) return;
    open?.({
      type: "success",
      message:
        result.value.mode === "deleted"
          ? "Ekip üyesi silindi."
          : "Ekip üyesi silindi. Kişisel bilgileri silindi; geçmiş işlemleri kayıtlarda kalır.",
    });
    navigate("/team");
  }

  const status = member ? STAFF_STATUS[member.status] : undefined;

  return (
    <Show
      title={member?.name ?? "Ekip üyesi"}
      isLoading={query.isLoading}
      canEdit={false}
      canDelete={false}
    >
      {invitation ? (
        <InvitationLink answer={invitation} onClose={() => setInvitation(null)} />
      ) : null}
      {member ? (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Durum">
                  <Tag color={status?.color}>{status?.label}</Tag>
                  {member.status === "active" && !member.twoFactorEnabled ? (
                    <Tag color="warning">Doğrulama kurulmadı</Tag>
                  ) : null}
                </Descriptions.Item>
                <Descriptions.Item label="E-posta">{member.email}</Descriptions.Item>
                <Descriptions.Item label="Eklenme">
                  {dayjs(member.createdAt).format(DATE_TIME_FORMAT)}
                </Descriptions.Item>
                {member.status === "invited" && member.invitationExpiresAt ? (
                  <Descriptions.Item label="Davet geçerliliği">
                    {dayjs(member.invitationExpiresAt).format(DATE_TIME_FORMAT)}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>

              {isSelf ? (
                <Alert
                  type="info"
                  showIcon
                  message="Bu senin hesabın. Kendi bilgilerini, rollerini ve durumunu burada değiştiremezsin; şifren için Hesabım'ı kullan."
                />
              ) : (
                <>
                  <Card size="small" title="Bilgiler">
                    <Form
                      form={profileForm}
                      layout="vertical"
                      requiredMark={false}
                      onFinish={async (values: ProfileValues) => {
                        const result = await run(
                          "profile",
                          () =>
                            api(`/api/office/staff/${id}`, {
                              method: "PUT",
                              body: { name: values.name, phoneNumber: values.phoneNumber ?? "" },
                            }),
                          "Bilgiler kaydedildi.",
                        );
                        if (result.ok) await refresh();
                      }}
                    >
                      <Form.Item
                        label="Ad Soyad"
                        name="name"
                        rules={[{ required: true, min: 2, message: "Ad soyad gir." }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item label="Cep telefonu" name="phoneNumber">
                        <Input inputMode="tel" placeholder="5XX XXX XX XX" />
                      </Form.Item>
                      <Button htmlType="submit" loading={busy === "profile"}>
                        Bilgileri kaydet
                      </Button>
                    </Form>
                  </Card>

                  <Card size="small" title="Roller">
                    <Form
                      form={rolesForm}
                      layout="vertical"
                      requiredMark={false}
                      onFinish={async (values: { roleIds: string[] }) => {
                        const result = await run(
                          "roles",
                          () =>
                            api(`/api/office/staff/${id}/roles`, { method: "PUT", body: values }),
                          "Roller güncellendi.",
                        );
                        if (result.ok) await refresh();
                      }}
                    >
                      <Form.Item
                        name="roleIds"
                        rules={[{ required: true, message: "En az bir rol seç." }]}
                      >
                        <Select
                          mode="multiple"
                          optionFilterProp="label"
                          aria-label="Roller"
                          options={roles.data.map((role) => ({ value: role.id, label: role.name }))}
                        />
                      </Form.Item>
                      <Button htmlType="submit" loading={busy === "roles"}>
                        Rolleri kaydet
                      </Button>
                    </Form>
                  </Card>
                </>
              )}
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
              {member.status === "invited" && !isSelf ? (
                <Card size="small" title="Davet">
                  <Typography.Paragraph type="secondary">
                    Yeni bağlantı öncekini geçersiz kılar.
                  </Typography.Paragraph>
                  <Space wrap>
                    <Button onClick={() => newInvitation(false)} loading={busy === "invite"}>
                      Yeni davet bağlantısı
                    </Button>
                    <Button
                      onClick={() => newInvitation(true)}
                      loading={busy === "invite"}
                      disabled={!member.phoneNumber}
                    >
                      SMS ile gönder
                    </Button>
                  </Space>
                </Card>
              ) : null}

              {!isSelf ? (
                <Card size="small" title="Erişim">
                  <Space wrap>
                    {member.isActive ? (
                      <Popconfirm
                        title="Devre dışı bırakılsın mı?"
                        description="Oturumları hemen kapanır ve giriş yapamaz. Sonra yeniden etkinleştirilebilir."
                        okText="Devre dışı bırak"
                        cancelText="Vazgeç"
                        onConfirm={async () => {
                          const result = await run(
                            "active",
                            () =>
                              api(`/api/office/staff/${id}/active`, {
                                method: "PUT",
                                body: { isActive: false },
                              }),
                            "Ekip üyesi devre dışı bırakıldı.",
                          );
                          if (result.ok) await refresh();
                        }}
                      >
                        <Button loading={busy === "active"}>Devre dışı bırak</Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        loading={busy === "active"}
                        onClick={async () => {
                          const result = await run(
                            "active",
                            () =>
                              api(`/api/office/staff/${id}/active`, {
                                method: "PUT",
                                body: { isActive: true },
                              }),
                            "Ekip üyesi etkinleştirildi.",
                          );
                          if (result.ok) await refresh();
                        }}
                      >
                        Etkinleştir
                      </Button>
                    )}
                    <Popconfirm
                      title="Ekipten silinsin mi?"
                      description="Geri alınamaz. Hiç işlem yapmadıysa tamamen silinir; işlem yaptıysa kişisel bilgileri silinir, işlemleri kayıtlarda kalır."
                      okText="Sil"
                      okButtonProps={{ danger: true }}
                      cancelText="Vazgeç"
                      onConfirm={remove}
                    >
                      <Button danger loading={busy === "remove"}>
                        Ekipten sil
                      </Button>
                    </Popconfirm>
                  </Space>
                </Card>
              ) : null}

              <Card size="small" title="Son işlemleri">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={member.recentActions}
                  locale={{ emptyText: "Henüz işlem yok" }}
                  scroll={{ x: "max-content" }}
                  columns={[
                    {
                      title: "Tarih",
                      dataIndex: "createdAt",
                      render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
                    },
                    {
                      title: "İşlem",
                      dataIndex: "action",
                      render: (value: string) => AUDIT_ACTIONS[value] ?? value,
                    },
                    {
                      title: "Hedef",
                      dataIndex: "targetType",
                      render: (value: string) => AUDIT_TARGETS[value] ?? value,
                    },
                  ]}
                />
              </Card>
            </Space>
          </Col>
        </Row>
      ) : null}
    </Show>
  );
}
